import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BRAND } from "../constants/brand";
import { useAuth } from "../context/AuthContext";
import { GoogleG } from "../components/marketing/SignupPopup";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name || user.email}`);
      const dest = user.role === "vendor" ? "/vendor/dashboard" : user.role === "customer" ? "/customer/dashboard" : "/auth/role-select";
      navigate(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  const goGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-neutral-950 text-white grain md:flex md:flex-col md:justify-between md:p-10">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-brand">
          <img src={BRAND.logo} alt="EarlyPrint" className="h-10 w-10 rounded-full" />
          <span className="font-display text-xl font-bold">Early<span className="text-lime-400">Print</span></span>
        </Link>
        <div>
          <h1 className="font-display text-5xl font-black leading-tight tracking-tight">Print anything. <span className="text-lime-400">From anywhere.</span></h1>
          <ul className="mt-6 space-y-2 text-sm text-neutral-300">
            <li>• Compare live prices from multiple verified vendors</li>
            <li>• AI-powered file checking before you pay</li>
            <li>• Live order tracking, escrow-safe payment</li>
          </ul>
        </div>
        <div className="text-xs text-neutral-500">© {new Date().getFullYear()} {BRAND.legalName} · {BRAND.location}</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-6 inline-flex items-center gap-2 md:hidden">
            <img src={BRAND.logo} alt="EarlyPrint" className="h-9 w-9 rounded-full" />
            <span className="font-display text-lg font-bold">Early<span className="text-lime-500">Print</span></span>
          </Link>

          <h2 className="font-display text-3xl font-bold" data-testid="login-title">Welcome back</h2>
          <p className="mt-1 text-sm text-neutral-600">Login to place orders or manage your shop.</p>

          <button onClick={goGoogle} className="mt-6 flex w-full items-center justify-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-3 text-sm font-semibold hover:bg-neutral-50" data-testid="login-google-btn">
            <GoogleG /> Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-neutral-400">
            <div className="h-px flex-1 bg-neutral-200" /> OR <div className="h-px flex-1 bg-neutral-200" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <input type="email" placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-neutral-300 bg-white px-3 py-3 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" data-testid="login-email" />
            <input type="password" placeholder="Password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-md border border-neutral-300 bg-white px-3 py-3 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" data-testid="login-password" />
            <button type="submit" disabled={busy} className="btn-offset w-full rounded-md bg-neutral-950 px-4 py-3 text-sm font-bold text-white hover:bg-neutral-800 disabled:opacity-60" data-testid="login-submit">{busy ? "Signing in…" : "Login"}</button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-600">New to EarlyPrint? <Link to="/signup" className="font-semibold text-neutral-900 hover:text-lime-500" data-testid="login-to-signup">Create account</Link></p>
          <p className="mt-2 text-center text-xs text-neutral-500">Test: customer@earlyprint.in / Test@1234</p>
        </div>
      </div>
    </div>
  );
}
